/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { useContext, useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate, useParams } from "react-router-dom";
import ActionOptions from "./ActionOptions";
import Input from "../Inputs/Input";
import Context from "../../../../store/context";
import { shortUuidRaw } from "../../../../helpers";
import labels from "../helpers/labels";
import actionOptionsConfig from "./actionOptionsConfig";
import HelpButton from "../../../../HelpContent/HelpButton";
import { useConfirmDialog } from "../../../Components/ConfirmDialogContext";
import Errors from "../../../Components/Errors";
import {
  faAngleDown,
  faAngleUp,
  faClone,
  faMinusCircle,
  faPlusCircle,
} from "@fortawesome/free-solid-svg-icons";

export default function Actions({ ruleState, setActions, event }) {
  const pageProps = JSON.parse(
    document.querySelector("#app").dataset.page
  ).props;
  const params = useParams();
  const navigate = useNavigate();
  const context = useContext(Context);
  const confirmDialog = useConfirmDialog();
  const { isModalSidebar } = context;

  const state = useMemo(
    () => [...(ruleState.Actions || [])].sort((a, b) => a.Order - b.Order),
    [ruleState.Actions]
  );
  const dispatch = (action) => {
    switch (action.type) {
      case "edit-action":
        setActions(
          state
            .map((a) => {
              if (a.UUID === action.actionId) {
                return { ...action.newAction };
              }
              return a;
            })
            .map((a, index) => ({ ...a, Order: index }))
        );

        break;
      case "add-action":
        setActions(
          [
            ...state,
            {
              UUID: shortUuidRaw(),
              Type: "",
              Options: [],
            },
          ].map((a, index) => ({ ...a, Order: index }))
        );
        break;
      case "clone-action":
        {
          // remove ID, UUID, Options from newAction
          const { ID, UUID, Options, ...newAction } = action.action || {};
          newAction.UUID = shortUuidRaw();
          // remove ActionID from options
          newAction.Options =
            action.action.Options?.map(({ ActionID, ...o }) => o) || [];
          setActions(
            [...state, newAction].map((a, index) => ({
              ...a,
              Order: index,
            }))
          );
        }
        break;
      case "up-action":
        {
          const actions = [...state];
          actions.splice(
            action.index - 1,
            0,
            actions.splice(action.index, 1)[0]
          );
          setActions([...actions].map((a, index) => ({ ...a, Order: index })));
        }
        break;
      case "down-action":
        {
          const actions = [...state];
          actions.splice(
            action.index + 1,
            0,
            actions.splice(action.index, 1)[0]
          );
          setActions([...actions].map((a, index) => ({ ...a, Order: index })));
        }
        break;
      case "remove-action":
        setActions(
          state
            .filter((a) => action.actionUUID !== a.UUID)
            .map((a, index) => ({ ...a, Order: index }))
        );
        break;
      default:
        setActions(state.map((a, index) => ({ ...a, Order: index })));
    }
  };

  const getActionDefaults = (action) => {
    if (action.Type) {
      return (
        actionOptionsConfig({
          action,
          context,
          pageProps,
          params,
          rule: ruleState,
          event,
        })[action.Type]?.inputs?.reduce(
          (p, c) => {
            if (
              c.type === "property" &&
              c.defaultValue !== undefined &&
              c.defaultValue !== null
            ) {
              return { ...p, [c.field]: c.defaultValue[0] };
            }
            if (
              c.type === "option" &&
              c.defaultValue !== undefined &&
              c.defaultValue !== null
            ) {
              return {
                ...p,
                Options: [
                  ...p.Options,
                  ...c.defaultValue.map((Value) => ({ Type: c.field, Value })),
                ],
              };
            }
            if (
              c.type === "compound" &&
              c.defaultValue !== undefined &&
              c.defaultValue !== null
            ) {
              return {
                ...p,
                Options: [
                  ...p.Options,
                  ...c.defaultValue.map((Value) =>
                    typeof Value === "object"
                      ? {
                          Type: c.field,
                          ValueFrom: "static",
                          Key: c.props.keyValue,
                          ...Value,
                        }
                      : {
                          Type: c.field,
                          Value,
                          ValueFrom: "static",
                          Key: c.props.keyValue,
                        }
                  ),
                ],
              };
            }
            return p;
          },
          { Options: [] }
        ) ?? {}
      );
    }
  };

  const typeChangeHandler = (action, actionType, hasOptions) => {
    // check for a connected port
    if (
      action.Type === "start:runtime" &&
      action.TypeUUID &&
      actionType !== "start:runtime"
    ) {
      toast.error(
        "Port is connected. Action can not be changed. Disconnect port first."
      );
      return;
    }
    dispatch({
      type: "edit-action",
      actionId: action.UUID,
      newAction: {
        UUID: action.UUID,
        Type: actionType,
        Options: [],
        ...getActionDefaults({ ...action, Type: actionType }),
      },
    });
    if (hasOptions) {
      navigate(
        `/library/logic/builder/${params.projectID}/workflow/${params.workflowUUID}/event/${params.event}/listener/${params.listenerUUID}/action/${action.UUID}`
      );
    }
  };

  const handleRemoveAction = async (action) => {
    // check for a connected port
    if (action.Type === "start:runtime" && action.TypeUUID) {
      toast.error(
        "Port is connected. Action can not be removed. Disconnect port first."
      );
      return;
    }
    if (action.Type) {
      const ans = await confirmDialog(
        "Delete action",
        `Are you sure you want to delete this action: "${labels[action.Type]}"?`
      );
      if (ans === false) {
        return;
      }
    }
    dispatch({
      type: "remove-action",
      actionUUID: action.UUID,
    });
  };

  const actionOptionTypes = useMemo(
    () =>
      Object.keys(
        actionOptionsConfig({
          action: {},
          context,
          pageProps,
          params,
          rule: ruleState,
        })
      ),
    []
  );

  useEffect(() => {
    if (!params.actionUUID) {
      return;
    }
    const newActions = (ruleState.Actions || []).map((action) => {
      const { Options: defaultOptions, ...defaults } =
        getActionDefaults(action) || {};
      return {
        ...defaults,
        ...action,
        Options: [
          ...action.Options,
          ...(defaultOptions?.filter(
            (defaultOption) =>
              !action.Options.some(
                (actOpt) =>
                  // add the default option only if an option of the same type and key doesn't already exist
                  actOpt.Type === defaultOption.Type &&
                  (actOpt.Key === defaultOption.Key || !defaultOption.Key)
              )
          ) || []),
        ],
      };
    });
    setActions(newActions);
  }, [params.actionUUID, context]);

  return (
    <div className="row">
      <div className={isModalSidebar ? "col-12" : "col-xl-2"}>
        Actions:
        <HelpButton helpKey="components-actions" />
        <button
          type="button"
          title="Add action"
          className="btn btn-outline-primary btn-sm float-end border-0"
          onClick={() => dispatch({ type: "add-action" })}
        >
          <FontAwesomeIcon icon={faPlusCircle} />
        </button>
      </div>
      <div className={isModalSidebar ? "col-12" : "col-xl-10"}>
        {state.length > 0 ? (
          state.map((action, index, actions) => (
            <div
              className="d-flex align-items-center mb-1 action"
              key={action.UUID}
            >
              {/* Action Type */}
              <div className="actions-select-type" style={{ minWidth: 300 }}>
                <Input
                  autoFocus={!action.Type}
                  inputType="select"
                  isSearchable
                  isValidNewOption={() => false}
                  inputHelp="Select type of action or type to search"
                  options={[
                    // show these options if event is form-render
                    ...(event === "form-render"
                      ? [
                          "form-question",
                          "form-message",
                          "form-complete-message",
                          "form-complete-early",
                        ]
                      : [
                          // show these options if event is message:reply
                          ...(event === "message:reply"
                            ? [
                                "AddResponderToDynamicContactSet",
                                "RemoveResponderFromDynamicContactSet",
                                "AddResponderToContactSet",
                                "RemoveResponderFromContactSet",
                                "StoreReplyToVariable",
                                "recipientToVariable",
                              ]
                            : []),
                          ...(event === "form-submitted"
                            ? [
                                "form:reject",
                                "form:redirect",
                                "form:reply-to-variable",
                                "form:book-event",
                                "recipientToVariable",
                              ]
                            : []),
                          ...(event === "component:package"
                            ? ["component:accept-package"]
                            : []),
                          "component:package",
                          "start:runtime",
                          "component:start",
                          "stop:runtime",
                          "stop:process",
                          ...(event === "outbound-delivery-status"
                            ? []
                            : ["start:broadcast"]),
                          "restart:runtime",
                          "setDynamicComms",
                          "FlowReturn",
                          "setMilestone",
                          "milestone:cancel",
                          "AddStarterToContactSet",
                          "RemoveStarterFromContactSet",
                          "AddStarterToDynamicContactSet",
                          "RemoveStarterFromDynamicContactSet",
                          "AddDynamicContactSetToContactSet",
                          "RemoveDynamicContactSetFromContactSet",
                          "RemoveAllFromDynamicContactSet",
                          "RemoveAllFromContactSet",
                          "StoreInputArgumentToDynamicContactSet",
                          "assets:context-clear",
                          "assets:contacts-to-variable",
                          "json:to-asset",
                          "assets:to-variable",
                          "extractText:inputArgument",
                          "UpdateVariable",
                          "ForwardEvent",
                          "SetCounter",
                          "time-zone:from-location",
                          "editContact",
                          "reporting:new-entry",
                          "reporting:load-entry",
                          "ticket:create",
                          "ticket:load",
                          "ticket:set-status",
                          "api:make-request",
                          "extract:from-data-structure",
                          "contact:load",
                          "contact:first-or-create",
                          "contact:delete",
                          "contact:get-location",
                          "contact:update-location",
                          "contact:add-to-group",
                          "contact:remove-from-group",
                          "startedByToVariable",
                          "mergeVariables",
                          "get-filenames",
                          "file:store-permanently",
                          "file:rows-to-json-variable",
                          "file:tag-files",
                          "subtractVariables",
                          "uniqueVariables",
                          "event:create",
                          "event:close-all",
                          "event:find-by-ticket",
                          "event:find-by-reference",
                          "event:find-by-user-reference",
                          "event:assets",
                          "event:tickets",
                          "event:contacts",
                          "event:update",
                          "event:status-update",
                          "event:locations",
                          "variables:move-one-to-another",
                          "variables:delete-one",
                          "ai:prompt",
                          "workflow:contacts-to-variable",
                          "asset:groups-to-variable",
                          "add-log-entry",
                        ]),
                  ].map((act) => ({ value: act, label: labels[act] }))}
                  value={[action.Type || "Select an action type"]}
                  onChange={([actionType]) => {
                    typeChangeHandler(
                      action,
                      actionType,
                      actionOptionTypes.includes(actionType)
                    );
                  }}
                />
              </div>
              {action.Type !== "" && actionOptionTypes.includes(action.Type) ? (
                <ActionOptions
                  action={action}
                  rule={ruleState}
                  dispatch={dispatch}
                  event={event}
                  context={context}
                />
              ) : (
                <span className="btn text-secondary disabled ms-1 border-0">
                  No options
                </span>
              )}
              <span className="flex-grow-1" />
              <div className="ms-1">
                <button
                  title="Clone action"
                  type="button"
                  className="btn btn-outline-primary border-0"
                  onClick={() =>
                    dispatch({
                      type: "clone-action",
                      action,
                    })
                  }
                >
                  <FontAwesomeIcon icon={faClone} />
                </button>
              </div>
              <div className="ms-1 d-flex flex-column justify-content-center">
                <button
                  title="Move action up"
                  type="button"
                  className="btn btn-outline-primary border-0 py-0 px-2"
                  style={{
                    lineHeight: "calc( var(--bs-btn-line-height) / 2)",
                  }}
                  disabled={index === 0}
                  onClick={() =>
                    dispatch({
                      type: "up-action",
                      index,
                    })
                  }
                >
                  <FontAwesomeIcon icon={faAngleUp} />
                </button>
                <button
                  title="Move action down"
                  type="button"
                  className="btn btn-outline-primary border-0 py-0 px-2"
                  style={{
                    lineHeight: "calc( var(--bs-btn-line-height) / 2)",
                  }}
                  disabled={index === actions.length - 1}
                  onClick={() =>
                    dispatch({
                      type: "down-action",
                      index,
                    })
                  }
                >
                  <FontAwesomeIcon icon={faAngleDown} />
                </button>
              </div>
              <div className="ms-1">
                <button
                  title="Remove action"
                  type="button"
                  className="btn btn-outline-danger border-0"
                  onClick={() => handleRemoveAction(action)}
                >
                  <FontAwesomeIcon icon={faMinusCircle} />
                </button>
              </div>
            </div>
          ))
        ) : (
          <div>
            No actions defined for this rule.{" "}
            <button
              type="button"
              title="Add action"
              className="btn btn-link p-0 mb-1"
              onClick={() => dispatch({ type: "add-action" })}
            >
              Add an action.
            </button>
          </div>
        )}
        <Errors
          filterFn={(err) =>
            err.rule?.UUID === ruleState.UUID && err.error.field === "actions"
          }
          linkDisabled
        />
      </div>
    </div>
  );
}
